import {
  type InfiniteData,
  type UseInfiniteQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { useCallback, useMemo } from "react";

import { toast } from "@/lib/use-toast";

import type {
  EmailTemplate,
  EmailTemplateCreate,
  EmailTemplateFilters,
  EmailTemplateUpdate,
} from "@/types/email-template";
import { Page } from "@/types/pagination";

import { requestData } from "@/api/api-config";

const EMPTY_PAGE: Page<EmailTemplate> = { data: [], total: 0 };

/** Templates are edited rarely, so a session mostly reads them once. */
const TEMPLATE_STALE_TIME = 1000 * 60 * 5;

/** Enough to fill the picker's scroll viewport without fetching a whole library. */
const PICKER_TEMPLATES_PER_PAGE = 25;

/**
 * Callers pass their own `page`/`perPage`. Omitting them asks the API for every
 * template, which is only ever right for a caller that has no way to paginate —
 * each list row carries a full `body`, so an unbounded fetch is expensive.
 */
async function listEmailTemplates(
  filters?: EmailTemplateFilters,
): Promise<Page<EmailTemplate> | null> {
  return await requestData<Page<EmailTemplate>>("email/templates", "get", {
    searchParams: { ...filters },
  });
}

async function getEmailTemplate(
  templateId: string,
): Promise<EmailTemplate | null> {
  return await requestData<EmailTemplate>(
    `email/templates/${templateId}`,
    "get",
  );
}

async function createEmailTemplate(
  payload: EmailTemplateCreate,
): Promise<EmailTemplate | null> {
  return await requestData<EmailTemplate>(
    "email/templates",
    "post",
    { json: payload },
    false,
  );
}

async function updateEmailTemplate(
  templateId: string,
  payload: EmailTemplateUpdate,
): Promise<EmailTemplate | null> {
  return await requestData<EmailTemplate>(
    `email/templates/${templateId}`,
    "put",
    { json: payload },
    false,
  );
}

async function archiveEmailTemplate(
  templateId: string,
): Promise<boolean | null> {
  return await requestData<boolean>(
    `email/templates/${templateId}`,
    "delete",
    {},
    false,
  );
}

export const emailTemplateQueryKeys = {
  all: ["email", "templates"] as const,
  list: (filters?: EmailTemplateFilters) =>
    [...emailTemplateQueryKeys.all, "list", filters] as const,
  // Segregated from `list`: the two hold different shapes (InfiniteData vs Page)
  // under the same filters. Both still sit under `all`, so a mutation's
  // invalidate sweeps them together.
  listInfinite: (filters?: EmailTemplateFilters) =>
    [...emailTemplateQueryKeys.all, "list", "infinite", filters] as const,
  detail: (templateId: string) =>
    [...emailTemplateQueryKeys.all, "detail", templateId] as const,
};

export const emailTemplateQueryOptions = {
  list: (filters?: EmailTemplateFilters) => ({
    queryKey: emailTemplateQueryKeys.list(filters),
    queryFn: () => listEmailTemplates(filters),
    staleTime: TEMPLATE_STALE_TIME,
  }),
  detail: (templateId: string) => ({
    queryKey: emailTemplateQueryKeys.detail(templateId),
    queryFn: () => getEmailTemplate(templateId),
  }),
};

type EmailTemplatesInfiniteOptions = Omit<
  UseInfiniteQueryOptions<
    Page<EmailTemplate>,
    Error,
    InfiniteData<Page<EmailTemplate>, number>,
    readonly unknown[],
    number
  >,
  "queryKey" | "queryFn" | "getNextPageParam" | "initialPageParam"
> & { perPage?: number };

/**
 * Flattened, page-at-a-time template list for the picker's infinite scroll.
 *
 * The picker can't paginate mid-compose, but it also can't fetch the whole
 * library — so it grows one page at a time as the user scrolls.
 */
export function useEmailTemplatesInfinite(
  filters: Omit<EmailTemplateFilters, "page" | "perPage">,
  options?: EmailTemplatesInfiniteOptions,
) {
  const { perPage: perPageOption, ...infiniteQueryOptions } = options ?? {};
  const perPage = perPageOption ?? PICKER_TEMPLATES_PER_PAGE;

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: emailTemplateQueryKeys.listInfinite({ ...filters, perPage }),
      queryFn: async ({ pageParam = 1 }) => {
        const response = await listEmailTemplates({
          ...filters,
          page: pageParam,
          perPage,
        });
        return response ?? EMPTY_PAGE;
      },
      getNextPageParam: (lastPage, allPages) => {
        const loadedCount = allPages.reduce(
          (sum, page) => sum + page.data.length,
          0,
        );
        return loadedCount < lastPage.total ? allPages.length + 1 : undefined;
      },
      initialPageParam: 1,
      staleTime: TEMPLATE_STALE_TIME,
      ...infiniteQueryOptions,
    });

  const templates = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data?.pages],
  );

  const handleFetchNextPage = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    templates,
    isLoading,
    isFetchingNextPage,
    hasNextPage: !!hasNextPage,
    fetchNextPage: handleFetchNextPage,
    totalTemplates: data?.pages?.[0]?.total ?? 0,
  };
}

export const useCreateEmailTemplateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: EmailTemplateCreate) => createEmailTemplate(payload),
    onError: () =>
      toast({
        title: "Error",
        description: "Failed to create email template",
        variant: "error",
      }),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: emailTemplateQueryKeys.all }),
  });
};

export const useUpdateEmailTemplateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      templateId,
      payload,
    }: {
      templateId: string;
      payload: EmailTemplateUpdate;
    }) => updateEmailTemplate(templateId, payload),
    onError: () =>
      toast({
        title: "Error",
        description: "Failed to update email template",
        variant: "error",
      }),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: emailTemplateQueryKeys.all }),
  });
};

export const useArchiveEmailTemplateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (templateId: string) => archiveEmailTemplate(templateId),
    onError: () =>
      toast({
        title: "Error",
        description: "Failed to archive email template",
        variant: "error",
      }),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: emailTemplateQueryKeys.all }),
  });
};
